<template>
  <div v-if="shouldShowBanner" class="pwa-install-wrap">
    <div
      class="pwa-install-banner"
      role="region"
      :aria-label="bannerAriaLabel"
    >
      <img
        class="pwa-install-logo"
        src="@/assets/icons/OhYesMainLogo.png"
        alt="Oh Yes"
      />
      <div class="pwa-install-copy">
        <strong>{{ bannerTitle }}</strong>
        <span>{{ bannerDescription }}</span>
      </div>
      <v-btn small depressed class="pwa-install-btn" @click="handleInstall">
        {{ installButtonLabel }}
      </v-btn>
      <v-btn
        icon
        small
        class="pwa-install-close"
        aria-label="Dismiss home screen prompt"
        @click="dismissBanner"
      >
        <v-icon size="18">mdi-close</v-icon>
      </v-btn>
    </div>

    <v-bottom-sheet v-model="showInstallGuide" max-width="420">
      <v-card class="install-guide-card">
        <v-card-title class="install-guide-title">
          <div class="install-guide-heading">
            <img
              class="install-guide-logo"
              src="@/assets/icons/OhYesMainLogo.png"
              alt="Oh Yes"
            />
            <strong>{{ guideTitle }}</strong>
          </div>
          <v-btn icon small @click="showInstallGuide = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="install-guide-steps">
          <p class="install-guide-note">{{ guideNote }}</p>
          <div
            v-for="step in manualInstallSteps"
            :key="step.text"
            class="install-guide-step"
          >
            <v-icon color="#1697f6">{{ step.icon }}</v-icon>
            <span>{{ step.text }}</span>
          </div>
        </v-card-text>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script>
const DISMISS_KEY = "ohyesPwaInstallDismissedSession";
const LEGACY_DISMISS_KEY = "ohyesPwaInstallDismissedAt";

export default {
  name: "PwaInstallBanner",
  data() {
    return {
      deferredInstallPrompt: null,
      isMobileViewport: false,
      isAppInstalled: false,
      showBanner: false,
      showInstallGuide: false,
      promptTimer: null,
    };
  },
  computed: {
    userAgent() {
      return window.navigator.userAgent || "";
    },
    isIos() {
      const isTouchMac =
        window.navigator.platform === "MacIntel" &&
        window.navigator.maxTouchPoints > 1;

      return /iphone|ipad|ipod/i.test(this.userAgent) || isTouchMac;
    },
    isAndroid() {
      return /android/i.test(this.userAgent);
    },
    isIosChrome() {
      return this.isIos && /crios/i.test(this.userAgent);
    },
    isIosFirefox() {
      return this.isIos && /fxios/i.test(this.userAgent);
    },
    isIosEdge() {
      return this.isIos && /edgios/i.test(this.userAgent);
    },
    isIosSafari() {
      return (
        this.isIos &&
        /safari/i.test(this.userAgent) &&
        !/crios|fxios|edgios/i.test(this.userAgent)
      );
    },
    appleDeviceName() {
      return /ipad/i.test(this.userAgent) ||
        (window.navigator.platform === "MacIntel" &&
          window.navigator.maxTouchPoints > 1)
        ? "iPad"
        : "iPhone";
    },
    bannerAriaLabel() {
      return this.isIos ? "Add Oh Yes to Home Screen" : "Install Oh Yes";
    },
    canShowInstallAction() {
      return (
        this.isIos ||
        this.isAndroid ||
        this.isMobileViewport ||
        !!this.deferredInstallPrompt
      );
    },
    bannerTitle() {
      if (this.isIos) {
        return `Add Oh Yes on ${this.appleDeviceName}`;
      }

      return this.deferredInstallPrompt ? "Install Oh Yes" : "Add Oh Yes";
    },
    bannerDescription() {
      if (this.isIos) {
        return "Use the Share menu to add it to your Home Screen";
      }

      if (this.deferredInstallPrompt) {
        return "Quick access from your home screen";
      }

      return "Add it from your browser menu";
    },
    installButtonLabel() {
      if (this.isIos) {
        return "Show steps";
      }

      return this.deferredInstallPrompt ? "Install" : "Steps";
    },
    guideTitle() {
      return this.isIos
        ? `Add to Home Screen on ${this.appleDeviceName}`
        : "Install on Android";
    },
    guideNote() {
      if (this.isIos) {
        return `${this.appleDeviceName} does not show a website install popup. Add Oh Yes manually from the browser Share menu, then open it from your Home Screen.`;
      }

      if (!this.deferredInstallPrompt) {
        return "If the native install popup is not shown, use your browser menu to add Oh Yes to the Home Screen.";
      }

      return "";
    },
    manualInstallSteps() {
      if (this.isIos) {
        if (this.isIosChrome) {
          return [
            {
              icon: "mdi-export-variant",
              text: "Tap the Share icon in Chrome",
            },
            {
              icon: "mdi-plus-box-outline",
              text: "Tap Add to Home Screen",
            },
            {
              icon: "mdi-check-circle-outline",
              text: "Tap Add",
            },
          ];
        }

        if (this.isIosFirefox || this.isIosEdge) {
          return [
            {
              icon: "mdi-dots-horizontal",
              text: "Open the browser menu",
            },
            {
              icon: "mdi-export-variant",
              text: "Tap Share",
            },
            {
              icon: "mdi-plus-box-outline",
              text: "Tap Add to Home Screen",
            },
          ];
        }

        return [
          {
            icon: "mdi-export-variant",
            text: this.isIosSafari
              ? "Tap the Share icon in Safari"
              : "Open the browser Share menu",
          },
          {
            icon: "mdi-plus-box-outline",
            text: "Scroll and tap Add to Home Screen",
          },
          {
            icon: "mdi-check-circle-outline",
            text: "Check the name is Oh Yes, then tap Add",
          },
        ];
      }

      return [
        {
          icon: "mdi-dots-vertical",
          text: "Tap the browser menu",
        },
        {
          icon: "mdi-cellphone",
          text: "Choose Install app or Add to Home screen",
        },
        {
          icon: "mdi-check-circle-outline",
          text: "Confirm Install",
        },
      ];
    },
    shouldShowBanner() {
      return (
        this.showBanner &&
        this.isMobileViewport &&
        !this.isAppInstalled &&
        this.canShowInstallAction
      );
    },
  },
  mounted() {
    this.clearLegacyDismissal();
    this.updateDeviceState();
    window.addEventListener("resize", this.handleViewportChange);
    window.addEventListener("orientationchange", this.handleViewportChange);
    window.addEventListener("beforeinstallprompt", this.captureInstallPrompt);
    window.addEventListener("appinstalled", this.markInstalled);

    this.scheduleBanner();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleViewportChange);
    window.removeEventListener("orientationchange", this.handleViewportChange);
    window.removeEventListener(
      "beforeinstallprompt",
      this.captureInstallPrompt,
    );
    window.removeEventListener("appinstalled", this.markInstalled);
    window.clearTimeout(this.promptTimer);
  },
  methods: {
    updateDeviceState() {
      const isNarrowScreen = window.matchMedia("(max-width: 820px)").matches;
      const isTouchTablet =
        window.matchMedia("(pointer: coarse)").matches &&
        window.matchMedia("(max-width: 1024px)").matches;

      this.isMobileViewport = isNarrowScreen || isTouchTablet;
      this.isAppInstalled =
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true;

      if (!this.isMobileViewport || this.isAppInstalled) {
        this.showBanner = false;
      }
    },
    handleViewportChange() {
      this.updateDeviceState();
      this.scheduleBanner();
    },
    captureInstallPrompt(event) {
      event.preventDefault();
      this.deferredInstallPrompt = event;
      this.scheduleBanner();
    },
    scheduleBanner() {
      if (
        this.wasDismissedThisSession() ||
        this.isAppInstalled ||
        !this.isMobileViewport ||
        !this.canShowInstallAction
      ) {
        return;
      }

      window.clearTimeout(this.promptTimer);
      this.promptTimer = window.setTimeout(() => {
        this.showBanner = true;
      }, 1200);
    },
    async handleInstall() {
      if (this.isIos) {
        this.showInstallGuide = true;
        return;
      }

      if (!this.deferredInstallPrompt) {
        this.showInstallGuide = true;
        return;
      }

      try {
        this.deferredInstallPrompt.prompt();
        const choice = await this.deferredInstallPrompt.userChoice;
        this.deferredInstallPrompt = null;

        if (choice && choice.outcome === "accepted") {
          this.markInstalled();
          return;
        }

        this.showInstallGuide = true;
      } catch {
        this.showInstallGuide = true;
      }
    },
    dismissBanner() {
      this.showBanner = false;
      this.setSessionDismissed();
    },
    markInstalled() {
      this.isAppInstalled = true;
      this.showBanner = false;
      this.deferredInstallPrompt = null;
    },
    wasDismissedThisSession() {
      try {
        return sessionStorage.getItem(DISMISS_KEY) === "true";
      } catch {
        return false;
      }
    },
    setSessionDismissed() {
      try {
        sessionStorage.setItem(DISMISS_KEY, "true");
      } catch {
        return;
      }
    },
    clearLegacyDismissal() {
      try {
        localStorage.removeItem(LEGACY_DISMISS_KEY);
      } catch {
        return;
      }
    },
  },
};
</script>

<style scoped>
.pwa-install-wrap {
  position: fixed;
  right: 12px;
  bottom: calc(86px + env(safe-area-inset-bottom));
  left: 12px;
  z-index: 10001;
  pointer-events: none;
}

.pwa-install-banner {
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 10px;
  max-width: 420px;
  margin: 0 auto;
  padding: 10px;
  border: 1px solid rgba(22, 151, 246, 0.2);
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.18);
  pointer-events: auto;
}

.pwa-install-logo {
  width: 54px;
  height: 54px;
  object-fit: contain;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 8px;
  padding: 5px;
  background: #ffffff;
}

.pwa-install-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  color: #0f172a;
  line-height: 1.2;
}

.pwa-install-copy strong {
  font-size: 14px;
  font-weight: 700;
}

.pwa-install-copy span {
  margin-top: 3px;
  color: #475569;
  font-size: 12px;
}

.pwa-install-btn {
  min-width: 70px !important;
  border-radius: 6px;
  background: #1697f6 !important;
  color: #ffffff !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.pwa-install-close {
  color: #64748b !important;
}

.install-guide-card {
  border-radius: 8px 8px 0 0;
}

.install-guide-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px 8px;
}

.install-guide-heading {
  display: grid;
  min-width: 0;
  gap: 8px;
  color: #0f172a;
  font-size: 16px;
  line-height: 1.2;
}

.install-guide-logo {
  width: 116px;
  height: 42px;
  object-fit: contain;
}

.install-guide-steps {
  display: grid;
  gap: 12px;
  padding: 12px 18px 22px !important;
}

.install-guide-note {
  margin: 0;
  color: #475569;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.45;
}

.install-guide-step {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  color: #0f172a;
  font-size: 15px;
  font-weight: 600;
}

@media (max-width: 360px) {
  .pwa-install-banner {
    grid-template-columns: 46px minmax(0, 1fr) auto;
  }

  .pwa-install-logo {
    width: 46px;
    height: 46px;
  }

  .pwa-install-close {
    display: none;
  }
}
</style>
